/**
 * Submit for Processing context – header button opens confirm dialog; on confirm, submit then logout.
 * Per AWAITING workflow doc §5: show button when payment added, allow_edit, VOID/AWAITING, !applicant_lock.
 */

import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '@/app/hooks'
import { setApplicationData } from '@/features/applicant/applicationSlice'
import * as applicationService from '@/lib/api/applicationService'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ValidationModal } from '@/components/ui/ValidationModal'
import {
  applicantConfirmDialogProps,
  applicantValidationModalProps,
} from '@/features/applicant/constants/applicantDialogProps'
import type { SubmitForProcessingContextValue } from '@/features/applicant/types/submitForProcessing'
import type { SubmitForProcessingProviderProps } from '@/features/applicant/types/components'

const BALANCE_DUE_MESSAGE = 'You have a balance due. When you submit the payment, then it will automatically submit your application updates for review.'
const CONFIRM_MESSAGE = "Resubmit Application? Are you ready to resubmit? Once you've resubmitted, your application will be locked and no longer editable."
const SUCCESS_MESSAGE = 'Application submitted successfully for Processing.'

export const SubmitForProcessingContext = createContext<SubmitForProcessingContextValue | null>(null)

export function SubmitForProcessingProvider({
  children,
  applicationData,
  onLogout,
  onSuccessNavigate: _onSuccessNavigate,
}: SubmitForProcessingProviderProps) {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'confirm' | 'success'>('confirm')
  const [logoutCountdown, setLogoutCountdown] = useState<number | null>(null)
  const logoutTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) clearInterval(logoutTimerRef.current)
    }
  }, [])

  const openConfirmDialog = useCallback(() => {
    setError(null)
    setStep('confirm')
    setOpen(true)
  }, [])

  const handleCancel = useCallback(() => {
    setOpen(false)
    setError(null)
  }, [])

  const handleContinue = useCallback(async () => {
    if (submitting) return
    if (!applicationData?.applicant_id) return
    setError(null)
    if (applicationService.hasUnpaidApplicationFeeInvoice(applicationData)) {
      setError(BALANCE_DUE_MESSAGE)
      return
    }
    setSubmitting(true)
    try {
      await applicationService.requestApprovalToProcess(applicationData.applicant_id, false)
      dispatch(setApplicationData({ ...applicationData, applicant_status: 'PENDING' }))
      setStep('success')
      setLogoutCountdown(5)
      logoutTimerRef.current = setInterval(() => {
        setLogoutCountdown(prev => {
          if (prev === null || prev <= 1) {
            if (logoutTimerRef.current) {
              clearInterval(logoutTimerRef.current)
              logoutTimerRef.current = null
            }
            onLogout()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to submit for processing.')
    } finally {
      setSubmitting(false)
    }
  }, [applicationData, dispatch, onLogout, submitting])

  const confirmMessage = error
    ? `${CONFIRM_MESSAGE}\n\n${error}`
    : CONFIRM_MESSAGE

  const successMessage =
    logoutCountdown !== null && logoutCountdown > 0
      ? `${SUCCESS_MESSAGE} You will be logged out in ${logoutCountdown} second${logoutCountdown !== 1 ? 's' : ''}.`
      : SUCCESS_MESSAGE

  return (
    <SubmitForProcessingContext.Provider value={{ openConfirmDialog }}>
      {children}
      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={open && step === 'confirm'}
        title="Resubmit Application?"
        message={confirmMessage}
        primaryLabel={submitting ? 'Submitting...' : 'Submit'}
        onPrimary={() => {
          if (submitting) return
          void handleContinue()
        }}
        secondaryLabel="Cancel"
        onSecondary={handleCancel}
        onDismiss={handleCancel}
        hideClose={submitting}
        overlayDataTestId="applicant-modal-submit-for-processing"
        primaryButtonDataTestId="applicant-modal-submit-for-processing-submit-btn"
        secondaryButtonDataTestId="applicant-modal-submit-for-processing-cancel-btn"
        closeButtonDataTestId="applicant-modal-submit-for-processing-close-btn"
      />
      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={open && step === 'success'}
        title="Success"
        message={successMessage}
        variant="success"
        showValidationIcon={false}
        overlayDataTestId="applicant-modal-submit-for-processing-success"
        confirmButtonDataTestId="applicant-modal-submit-for-processing-success-ok-btn"
        onClose={() => {
          if (logoutTimerRef.current) {
            clearInterval(logoutTimerRef.current)
            logoutTimerRef.current = null
          }
          onLogout()
        }}
      />
    </SubmitForProcessingContext.Provider>
  )
}
